import onDOMContentLoaded from "../../base/util/dom/onDOMContentLoaded";

interface SubaccountSearchElements {
    container: HTMLElement;
    searchInput: HTMLInputElement;
    accordion: HTMLElement;
}

interface SubaccountSearchConfig {
    searchTargetSelector?: string;
    itemSelector?: string;
    hideClassName?: string;
    debounceMs?: number;
}

class SubaccountSearch {
    private readonly elements: SubaccountSearchElements;
    private readonly config: Required<SubaccountSearchConfig>;
    private originalHeight: number = 0;
    private debounceTimer?: number;
    private boundHandleSearch: ((event: Event) => void) | null = null;
    private isDestroyed: boolean = false;

    constructor(elements: SubaccountSearchElements, config: SubaccountSearchConfig = {}) {
        this.elements = elements;

        // Set default configuration
        this.config = {
            searchTargetSelector: '.learner-full-name',
            itemSelector: '.as__accordion__item',
            hideClassName: 'as__hide',
            debounceMs: 300,
            ...config
        };

        this.initialize();
    }

    private initialize(): void {
        try {
            this.setupAccordion();
            this.initializeEventListeners();
        } catch (error) {
            console.error('SubaccountSearch initialization failed:', error);
            this.showAllItems(); // Fallback: show all items if initialization fails
        }
    }

    private setupAccordion(): void {
        try {
            // Ensure all items are visible initially
            this.showAllItems();

            // Store original height for consistent layout
            this.originalHeight = this.elements.accordion.scrollHeight;

            if (this.originalHeight > 0) {
                this.elements.accordion.style.minHeight = `${this.originalHeight}px`;
            }
        } catch (error) {
            console.error('Error setting up accordion:', error);
        }
    }

    private initializeEventListeners(): void {
        try {
            // Create bound function for cleanup
            this.boundHandleSearch = this.handleSearch.bind(this);

            // Listen to input event for better UX
            this.elements.searchInput.addEventListener('input', this.boundHandleSearch);
            this.elements.searchInput.addEventListener('paste', this.boundHandleSearch);
        } catch (error) {
            console.error('Error initializing event listeners:', error);
        }
    }

    private handleSearch(): void {
        if (this.isDestroyed) return;

        // Clear existing timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        // Set new debounce timer
        this.debounceTimer = window.setTimeout(() => {
            this.performSearch();
        }, this.config.debounceMs);
    }

    private performSearch(): void {
        if (this.isDestroyed) return;

        try {
            const searchValue = this.elements.searchInput.value.toLowerCase().trim();
            const accordionItems = this.elements.accordion.querySelectorAll(this.config.itemSelector);

            let visibleCount = 0;

            accordionItems.forEach((item) => {
                const htmlItem = item as HTMLElement;
                const isVisible = this.shouldItemBeVisible(htmlItem, searchValue);

                if (isVisible) {
                    this.showItem(htmlItem);
                    visibleCount++;
                } else {
                    this.hideItem(htmlItem);
                }
            });

            // Maintain accordion height for stable layout
            this.maintainAccordionHeight();
        } catch (error) {
            console.error('Search operation failed:', error);
            this.showAllItems(); // Fallback: show all items if search fails
        }
    }

    private shouldItemBeVisible(item: HTMLElement, searchValue: string): boolean {
        // Empty search shows all items
        if (searchValue === '') {
            return true;
        }

        try {
            const targetElement = item.querySelector(this.config.searchTargetSelector) as HTMLElement | null;

            if (!targetElement?.textContent) {
                return false;
            }

            return targetElement.textContent.toLowerCase().includes(searchValue);
        } catch (error) {
            console.error('Error checking item visibility:', error);
            return true; // Show item if check fails
        }
    }

    private showItem(item: HTMLElement): void {
        item.classList.remove(this.config.hideClassName);
        item.setAttribute('aria-hidden', 'false');
    }

    private hideItem(item: HTMLElement): void {
        item.classList.add(this.config.hideClassName);
        item.setAttribute('aria-hidden', 'true');
    }

    private showAllItems(): void {
        const items = this.elements.accordion.querySelectorAll(this.config.itemSelector);
        items.forEach(item => this.showItem(item as HTMLElement));
    }

    private maintainAccordionHeight(): void {
        if (this.originalHeight > 0) {
            this.elements.accordion.style.height = `${this.originalHeight}px`;
        }
    }

    public destroy(): void {
        if (this.isDestroyed) return;

        this.isDestroyed = true;

        // Clear debounce timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        // Remove event listeners
        if (this.boundHandleSearch) {
            this.elements.searchInput.removeEventListener('input', this.boundHandleSearch);
            this.elements.searchInput.removeEventListener('paste', this.boundHandleSearch);
            this.boundHandleSearch = null;
        }

        // Reset accordion
        this.elements.accordion.style.minHeight = '';
        this.elements.accordion.style.height = '';
        this.showAllItems();
    }

    static initialize(config?: SubaccountSearchConfig): SubaccountSearch | null {
        try {
            const elements = this.getRequiredElements();

            if (!elements) {
                return null;
            }

            return new SubaccountSearch(elements, config);
        } catch (error) {
            console.error('Failed to initialize SubaccountSearch:', error);
            return null;
        }
    }

    private static getRequiredElements(): SubaccountSearchElements | null {
        const container = document.querySelector('.as__subaccount') as HTMLElement | null;

        if (!container) {
            return null;
        }

        const searchInput = container.querySelector('.dws__subaccount-search .as__input') as HTMLInputElement | null;
        const accordion = container.querySelector('.dws__m54-accordion') as HTMLElement | null;

        if (!searchInput || !accordion) {
            return null;
        }

        return { container, searchInput, accordion };
    }
}

// Store instance for cleanup
let searchInstance: SubaccountSearch | null = null;

// Initialize on DOM ready
onDOMContentLoaded(() => {
    let element = document.querySelector('.as__subaccount');

    if (!element) {
        return;
    }

    searchInstance = SubaccountSearch.initialize({
        debounceMs: 300
    });
});

// Clean up on page unload
window.addEventListener('beforeunload', () => {
    if (searchInstance) {
        searchInstance.destroy();
        searchInstance = null;
    }
});